/**
 * Blocco B5.5 — Pannello "Ritiri da recuperare" (admin/ufficio).
 *
 * Mostra elenco recuperi con filtri (stato, zona) e azioni:
 *  - Riprogramma su giro esistente
 *  - Annulla con motivo
 *
 * Trasportatore: questa pagina NON è esposta (protected route admin/ufficio).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import {
  listRecuperi, riprogrammaRecupero, annullaRecupero, getGiriOperativiZone,
} from "../lib/api";
import { useTutteLeZone } from "../hooks/useZone";
import {
  STATO_RECUPERO_LABEL, STATO_RECUPERO_COLOR,
  MOTIVO_RECUPERO_LABEL, FASCE_VISITA_LABEL,
} from "../lib/constants";
import { RefreshCw, Calendar as CalendarIcon, Phone } from "lucide-react";

export default function RecuperiList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("APERTO");
  const [filtroZona, setFiltroZona] = useState("");
  const [dlgReprog, setDlgReprog] = useState(null);
  const [dlgCancel, setDlgCancel] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtroStato && filtroStato !== "ALL") params.stato = filtroStato;
      if (filtroZona) params.zona = filtroZona;
      const d = await listRecuperi(params);
      setItems(d);
    } catch {
      toast.error("Errore caricamento recuperi");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filtroStato, filtroZona]);

  // Fonte canonica delle zone: anagrafica /api/zone (NON derivata dai risultati
  // filtrati). Così cambiando filtro Zona restano visibili tutte le altre zone.
  const { zone: zoneAnagrafica } = useTutteLeZone();
  const zoneList = useMemo(() => {
    return (zoneAnagrafica || []).map((z) => z.nome).sort();
  }, [zoneAnagrafica]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-slate-900">Ritiri da recuperare</h1>
        <Button variant="outline" onClick={load} data-testid="recuperi-refresh">
          <RefreshCw size={16} className="mr-2" /> Aggiorna
        </Button>
      </div>

      <Card className="p-4">
        <div className="flex flex-wrap gap-3 items-end">
          <div>
            <Label className="text-xs">Stato</Label>
            <Select value={filtroStato} onValueChange={setFiltroStato}>
              <SelectTrigger className="w-44" data-testid="recuperi-filtro-stato">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Tutti</SelectItem>
                <SelectItem value="APERTO">Aperti</SelectItem>
                <SelectItem value="RIPROGRAMMATO">Riprogrammati</SelectItem>
                <SelectItem value="RECUPERATO">Recuperati</SelectItem>
                <SelectItem value="ANNULLATO">Annullati</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Zona</Label>
            <Select value={filtroZona || "ALL"} onValueChange={(v) => setFiltroZona(v === "ALL" ? "" : v)}>
              <SelectTrigger className="w-52" data-testid="recuperi-filtro-zona">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL" data-testid="recuperi-filtro-zona-tutte">Tutte</SelectItem>
                {zoneList.map((z) => (
                  <SelectItem key={z} value={z} data-testid={`recuperi-filtro-zona-${z}`}>{z}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="text-sm text-slate-600 ml-auto">
            {loading ? "Caricamento…" : `${items.length} recuperi`}
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((r) => (
          <RecuperoCard
            key={r.id}
            recupero={r}
            onReprog={() => setDlgReprog(r)}
            onCancel={() => setDlgCancel(r)}
          />
        ))}
        {!loading && items.length === 0 && (
          <div className="col-span-full text-center py-12 text-slate-400 italic">
            Nessun recupero in lista.
          </div>
        )}
      </div>

      {dlgReprog && (
        <RiprogrammaDialog
          recupero={dlgReprog}
          onClose={() => setDlgReprog(null)}
          onDone={async () => { setDlgReprog(null); await load(); }}
        />
      )}
      {dlgCancel && (
        <AnnullaDialog
          recupero={dlgCancel}
          onClose={() => setDlgCancel(null)}
          onDone={async () => { setDlgCancel(null); await load(); }}
        />
      )}
    </div>
  );
}

function RecuperoCard({ recupero, onReprog, onCancel }) {
  const snap = recupero.snapshot || {};
  const stato = recupero.stato || "APERTO";
  const color = STATO_RECUPERO_COLOR[stato];
  return (
    <Card className="p-3 space-y-2" data-testid={`recupero-card-${recupero.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold text-sm truncate">{snap.ragioneSociale || "(?)"}</div>
          <div className="text-xs text-slate-500 truncate">
            {snap.indirizzo}{snap.cap ? `, ${snap.cap}` : ""}{snap.citta ? ` ${snap.citta}` : ""}
          </div>
        </div>
        <Badge style={{ backgroundColor: color, color: "#fff" }} data-testid={`recupero-stato-${recupero.id}`}>
          {STATO_RECUPERO_LABEL[stato]}
        </Badge>
      </div>
      <div className="flex flex-wrap gap-2 text-xs">
        <Badge variant="outline">{snap.zona || recupero.zonaOrigine}</Badge>
        {snap.fasciaOrariaVisita && snap.fasciaOrariaVisita !== "SEMPRE_APERTO" && (
          <Badge variant="outline">{FASCE_VISITA_LABEL[snap.fasciaOrariaVisita] || snap.fasciaOrariaVisita}</Badge>
        )}
        <Badge variant="outline" className="bg-slate-50">
          <CalendarIcon size={11} className="mr-1" />
          orig. {recupero.dataOrigine}
        </Badge>
      </div>
      <div className="text-xs">
        <span className="text-slate-500">Motivo: </span>
        <strong>{MOTIVO_RECUPERO_LABEL[recupero.motivo] || recupero.motivo}</strong>
        {recupero.note && (
          <div className="text-slate-500 italic mt-1">&ldquo;{recupero.note}&rdquo;</div>
        )}
      </div>
      {(snap.telefonoMobile || snap.telefonoFisso) && (
        <div className="text-xs text-slate-500 flex items-center gap-1">
          <Phone size={11} /> {snap.telefonoMobile || snap.telefonoFisso}
        </div>
      )}
      {recupero.dataDestinazione && (
        <div className="text-xs text-blue-700">
          ↪ Riprogrammato su <strong>{recupero.dataDestinazione}</strong>
        </div>
      )}
      {(stato === "APERTO" || stato === "RIPROGRAMMATO") && (
        <div className="flex gap-2 pt-1">
          {stato === "APERTO" && (
            <Button size="sm" variant="default" className="text-xs h-8" onClick={onReprog} data-testid={`recupero-riprogramma-${recupero.id}`}>
              Riprogramma
            </Button>
          )}
          <Button size="sm" variant="outline" className="text-xs h-8 text-red-600" onClick={onCancel} data-testid={`recupero-annulla-${recupero.id}`}>
            Annulla
          </Button>
        </div>
      )}
    </Card>
  );
}

function RiprogrammaDialog({ recupero, onClose, onDone }) {
  const [dataDest, setDataDest] = useState(new Date().toISOString().slice(0, 10));
  const [giroDest, setGiroDest] = useState("");
  const [giriDisponibili, setGiriDisponibili] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Carica giri operativi (source="zone") per la data scelta.
  // NESSUN fallback Legacy: se non esistono giri operativi per quella data,
  // l'utente deve prima creare il giro dalla Preview da zone.
  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      try {
        const zoneRes = await getGiriOperativiZone(dataDest).catch(() => null);
        const all = [];
        (zoneRes?.mezzi || []).forEach((m) => {
          if (m.giroId && ["PIANIFICATO", "IN_CORSO"].includes(m.stato)) {
            all.push({
              id: m.giroId,
              label: `${m.zona} · ${m.mezzo?.id ? (m.mezzo?.nome || "—") : "mezzo da assegnare"}`,
              source: "zone",
            });
          }
        });
        if (!cancel) setGiriDisponibili(all);
      } catch { /* ignore */ }
      finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [dataDest]);

  const confirm = async () => {
    if (!giroDest) { toast.error("Seleziona un giro destinazione"); return; }
    setSubmitting(true);
    try {
      await riprogrammaRecupero(recupero.id, { giroDestinazioneId: giroDest });
      toast.success("Recupero riprogrammato");
      await onDone();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore riprogrammazione");
    } finally { setSubmitting(false); }
  };

  const noGiri = !loading && giriDisponibili.length === 0;

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialog-riprogramma-recupero">
        <DialogHeader>
          <DialogTitle>Riprogramma recupero</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm">
            <strong>{recupero.snapshot?.ragioneSociale}</strong> · {recupero.snapshot?.zona || recupero.zonaOrigine}
          </div>
          <div>
            <Label>Data destinazione</Label>
            <Input
              type="date"
              value={dataDest}
              onChange={(e) => setDataDest(e.target.value)}
              data-testid="riprogramma-data-dest"
            />
          </div>
          <div>
            <Label>Giro destinazione</Label>
            <Select value={giroDest} onValueChange={setGiroDest} disabled={noGiri || loading}>
              <SelectTrigger data-testid="riprogramma-giro-dest">
                <SelectValue placeholder={loading ? "Caricamento…" : (noGiri ? "Nessun giro operativo per questa data" : "Seleziona giro")} />
              </SelectTrigger>
              <SelectContent>
                {giriDisponibili.map((g) => (
                  <SelectItem key={g.id} value={g.id} data-testid={`riprogramma-giro-option-${g.id}`}>{g.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {noGiri && (
              <p className="text-xs text-amber-700 mt-2" data-testid="riprogramma-no-giro-hint">
                Non esiste un giro operativo per questa data.
                Vai a <strong>Giro → Preview da zone</strong>, scegli la data e crea i giri operativi.
              </p>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Annulla</Button>
          <Button
            onClick={confirm}
            disabled={submitting || !giroDest || noGiri || loading}
            data-testid="riprogramma-conferma"
          >
            Riprogramma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function AnnullaDialog({ recupero, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const confirm = async () => {
    setSubmitting(true);
    try {
      await annullaRecupero(recupero.id, { motivo });
      toast.success("Recupero annullato");
      await onDone();
    } catch (ex) {
      toast.error(ex.response?.data?.detail || "Errore annullamento");
    } finally { setSubmitting(false); }
  };
  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="dialog-annulla-recupero">
        <DialogHeader>
          <DialogTitle>Annulla recupero</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-sm">
            <strong>{recupero.snapshot?.ragioneSociale}</strong>
          </div>
          <div>
            <Label>Motivo annullamento</Label>
            <Textarea
              rows={3}
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="(facoltativo)"
              data-testid="annulla-motivo"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={submitting}>Indietro</Button>
          <Button onClick={confirm} disabled={submitting} variant="destructive" data-testid="annulla-conferma">
            Annulla recupero
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
